import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { articles } from '@/mocks/data';
import { generateArticleSchema, generateBreadcrumbSchema } from '@/mocks/schema';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));
  const related = articles.filter((a) => a.id !== Number(id) && a.category === article?.category).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-xl text-foreground-900 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <JsonLd data={generateArticleSchema({
        title: article.title,
        description: article.excerpt,
        author: article.author,
        date: article.date,
        url: `https://guangzhousoap.cn/article/${article.id}`,
        image: article.image,
      }) as unknown as Record<string, unknown>} />
      <JsonLd data={generateBreadcrumbSchema([
        { name: '首页', url: 'https://guangzhousoap.cn' },
        { name: article.categoryName, url: `https://guangzhousoap.cn/category/${article.category}` },
        { name: article.title, url: `https://guangzhousoap.cn/article/${article.id}` },
      ]) as unknown as Record<string, unknown>} />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <article className="pt-20 pb-14 md:pb-20">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors">{article.categoryName}</Link>
              <span>/</span>
              <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
            </nav>

            {/* Article Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
              <div className="rounded-lg overflow-hidden h-64 md:h-80 mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 姨母的诱惑`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Article Content */}
            <div
              className="prose-content text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Article Footer */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-4 mb-8">
                <span className="text-sm text-foreground-500">分享到：</span>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow">
                  <i className="ri-twitter-x-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow">
                  <i className="ri-link"></i>
                </a>
              </div>
            </div>

            {/* Related Articles */}
            {related.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/article/${r.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                    >
                      <div className="h-28 rounded-lg overflow-hidden mb-3">
                        <img
                          src={r.image}
                          alt={r.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {r.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{r.author} · {r.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <style>{`
        .prose-content h3 {
          font-family: var(--font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .prose-content p {
          font-size: 0.95rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          margin-bottom: 1.25rem;
        }
        .prose-content strong {
          color: oklch(var(--foreground-900));
          font-weight: 600;
        }
      `}</style>
      <Footer />
    </>
  );
}